// jspdf + html2canvas build to ~595kB (177kB gzip) and nothing needs them until
// someone actually exports a PDF, so both are imported dynamically at the point of
// use rather than shipped in the initial bundle. jsPDF stays as a type-only import
// so signatures below still describe it; the runtime constructor is pulled in as
// `JsPDF` inside the exported functions.
import type { jsPDF } from "jspdf";
import type { Portal, Section } from "../types";
import { sectionToHtml } from "./sectionToHtml";

function pdfFilename(filename: string): string {
	const t = filename.trim();
	if (t.toLowerCase().endsWith(".pdf")) return t;
	return `${t.replace(/\.(doc|docx)$/i, "")}.pdf`;
}

function escapeHtml(input: string): string {
	return input
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
}

const EXPORT_STYLES = `
  * { box-sizing: border-box; }
  body { margin: 0; padding: 16px; font-family: Calibri, Arial, Helvetica, sans-serif; font-size: 11pt; color: #222; line-height: 1.5; background: #fff; }
  h1 { font-size: 20pt; margin: 16px 0 8px; }
  h2 { font-size: 16pt; margin: 14px 0 6px; }
  h3 { font-size: 13pt; margin: 12px 0 4px; }
  p { margin: 4px 0; }
  img { max-width: 100%; height: auto; }
  ul, ol { margin: 4px 0 4px 24px; }
  table { border-collapse: collapse; width: 100%; margin: 0.6em 0; }
  th, td { border: 1px solid #d1d5db; padding: 4pt 6pt; vertical-align: top; }
  th { background: #f3f4f6; text-align: left; }
`;

function waitForImages(root: Document | HTMLElement): Promise<void> {
	const imgs = Array.from(root.querySelectorAll("img"));
	return Promise.all(
		imgs.map(
			(img) =>
				new Promise<void>((resolve) => {
					if (img.complete) {
						resolve();
						return;
					}
					img.addEventListener("load", () => resolve(), { once: true });
					img.addEventListener("error", () => resolve(), { once: true });
				}),
		),
	).then(() => undefined);
}

const LAYOUT = {
	marginMm: 10,
	pageWidthMm: 210,
	pageHeightMm: 297,
} as const;

function innerDimensions() {
	const { marginMm, pageWidthMm, pageHeightMm } = LAYOUT;
	return {
		marginMm,
		innerW: pageWidthMm - 2 * marginMm,
		innerH: pageHeightMm - 2 * marginMm,
	};
}

/**
 * Renders one HTML fragment in an off-screen iframe and returns a canvas (same pipeline as a single-section PDF).
 */
async function captureHtmlToCanvas(html: string): Promise<HTMLCanvasElement> {
	const iframe = document.createElement("iframe");
	iframe.setAttribute("title", "user-guide-pdf-export");
	iframe.setAttribute(
		"style",
		"position:fixed;left:-4000px;top:0;width:816px;height:1400px;border:0;opacity:1;visibility:visible;pointer-events:none;z-index:0;",
	);
	document.body.appendChild(iframe);

	const doc = iframe.contentDocument;
	if (!doc) {
		iframe.remove();
		throw new Error("PDF export: iframe document unavailable.");
	}

	doc.open();
	doc.write(
		`<!DOCTYPE html><html><head><meta charset="utf-8"/><style>${EXPORT_STYLES}</style></head><body><div id="user-guide-export-root">${html}</div></body></html>`,
	);
	doc.close();

	const root = doc.getElementById("user-guide-export-root");
	if (!root) {
		iframe.remove();
		throw new Error("PDF export: root node missing.");
	}

	try {
		void root.offsetHeight;
		await waitForImages(root);
		if (iframe.contentWindow?.document.fonts?.ready) {
			await iframe.contentWindow.document.fonts.ready.catch(() => undefined);
		}
		await new Promise<void>((resolve) => {
			requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
		});

		const html2canvas = (await import("html2canvas")).default;
		return await html2canvas(root, {
			scale: 2,
			useCORS: true,
			logging: false,
			allowTaint: true,
			backgroundColor: "#ffffff",
			windowWidth: root.scrollWidth,
			windowHeight: root.scrollHeight,
		});
	} finally {
		iframe.remove();
	}
}

/**
 * Slices one tall canvas onto the PDF using multiple A4 pages. Mutates `sliceState` so multiple sections chain correctly.
 */
function addCanvasSlicesToPdf(
	pdf: jsPDF,
	canvas: HTMLCanvasElement,
	sliceState: { isFirstSlice: boolean },
): void {
	const { marginMm, innerW, innerH } = innerDimensions();
	const pxPerPage = Math.floor((canvas.width * innerH) / innerW);
	const nPages = Math.max(1, Math.ceil(canvas.height / pxPerPage));

	const pageCanvas = document.createElement("canvas");
	const pageCtx = pageCanvas.getContext("2d");
	if (!pageCtx) {
		throw new Error("PDF export: canvas 2D context unavailable.");
	}
	pageCanvas.width = canvas.width;

	for (let page = 0; page < nPages; page++) {
		if (!sliceState.isFirstSlice) {
			pdf.addPage();
		}
		sliceState.isFirstSlice = false;

		const srcY = page * pxPerPage;
		const slicePx = Math.min(pxPerPage, canvas.height - srcY);
		const sliceHeightMm = (slicePx * innerW) / canvas.width;

		pageCanvas.height = slicePx;
		pageCtx.fillStyle = "#ffffff";
		pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
		pageCtx.drawImage(
			canvas,
			0,
			srcY,
			canvas.width,
			slicePx,
			0,
			0,
			pageCanvas.width,
			pageCanvas.height,
		);

		const imgData = pageCanvas.toDataURL("image/jpeg", 0.92);
		pdf.addImage(imgData, "JPEG", marginMm, marginMm, innerW, sliceHeightMm);
	}
}

/**
 * Single HTML blob (e.g. one section) → PDF download.
 */
export async function exportToPdf(html: string, filename: string): Promise<void> {
	const { jsPDF: JsPDF } = await import("jspdf");
	const pdf = new JsPDF({
		unit: "mm",
		format: "a4",
		orientation: "portrait",
	});
	const canvas = await captureHtmlToCanvas(html);
	const sliceState = { isFirstSlice: true };
	addCanvasSlicesToPdf(pdf, canvas, sliceState);
	pdf.save(pdfFilename(filename));
}

/**
 * Full manual: rasterize **each section separately** (same content as the in-app guide / generated from the combined MD),
 * then append all pages into one PDF. One giant canvas for the whole manual hits browser canvas limits → blank pages.
 */
export async function exportFullManualPdf(
	sections: Section[],
	manualTitle: string,
	portal: Portal,
	filename: string,
): Promise<void> {
	const { jsPDF: JsPDF } = await import("jspdf");
	const pdf = new JsPDF({
		unit: "mm",
		format: "a4",
		orientation: "portrait",
	});
	const sliceState = { isFirstSlice: true };

	for (let i = 0; i < sections.length; i++) {
		const section = sections[i]!;
		let html = await sectionToHtml(section, portal);
		if (i === 0) {
			html = `<h1>${escapeHtml(manualTitle)}</h1>\n${html}`;
		}
		const canvas = await captureHtmlToCanvas(html);
		addCanvasSlicesToPdf(pdf, canvas, sliceState);
	}

	pdf.save(pdfFilename(filename));
}
